// DECISION-050 §2/§3 — instance transform precedence. PURE (no THREE / no DOM) so the ratified
// contract is unit-tested exactly as written.
//
// §2 (verbatim): "The mint composes each instance transform as `row.<field> ?? shellAnchor.<field>`.
// Default: transform-less instances (store only fixtureKind + anchorName + knobs) → re-derive from
// the current shell anchor every mint → can never go stale. Free-place / deliberate variant: opt-in
// — the instance persists a transform + a provenance/placement-mode flag, explicitly 'owns its
// transform, won't track the shell.' A transform on an instance exists ONLY because someone
// deliberately free-placed it."
//
// §3: "Footprint ≠ transform scale … Instances are authored at identity scale … The render layer
// IGNORES instance/anchor scale while any legacy footprint lingers." → scale is deliberately NOT
// resolved here; callers keep the anchor's own scale.
//
// ASK-087 Phase-1 slice-2 applies this RUNTIME-side (at compose, from hydrated /instances) ahead of
// Forge's Phase-B mint-side cutover. Same rule at two points = idempotent, so when the boot starts
// carrying the composed transform, "row wins" applied twice is a no-op (Coord seam ruling).

/** The ratified free-place markers. Ride in the instance's `knobs` (the only extensible
 * primitive-valued slot on the existing /instances write-shape). merchant-place / merchant-move come
 * from the WS3 ghost-controller commit spec (Client-written); prefab-seed is the Forge mint's stamp on
 * baked prefab instances — added to the Client reader so a prefab-seeded transform is HONOURED, not
 * ignored as unflagged (aligns with GCG's transform-write guard vocab, contracts/instance-write-fixture.json). */
// ASK-094 94a: `merchant-scale` joins the free-place vocabulary — a uniform-scale write is a
// transform-write (it changes the composed instance), so it carries `knobs.provenance` under the
// same §2 rule as move/place. (The scale FIELD shape is pending Coord contract review — see the
// 94a report; the provenance vocab is unambiguous and lands now.)
export const FREE_PLACE_PROVENANCE = ["merchant-place", "merchant-move", "prefab-seed", "merchant-scale"];

/** Does this instance row deliberately own its transform? §2: a transform exists ONLY via an opt-in
 * free-place, and it must carry the provenance/placement-mode flag — in `knobs.provenance`, the
 * ratified canonical home (2026-07-23 ruling, dual-stamp dropped). A TOP-LEVEL `row.provenance` is
 * deliberately NOT honoured: GCG's wire-guard rejects such writes (instance-write-fixture guard
 * case 4), so a read-side fallback would accept rows the contract says cannot exist — the exact
 * drift-class tolerance the ruling closed. Caught by test-instance-write-fixture when the folded
 * GCG guard cases landed.*/
export function isFreePlaced(row) {
	const p = row?.knobs?.provenance ?? null;
	return typeof p === "string" && FREE_PLACE_PROVENANCE.includes(p);
}

/** True when the row carries any transform field at all (position or yaw). */
export function hasTransform(row) {
	// ASK-094: `uniformScale` (the additive scalar the render honours — Coord ruling A, distinct from
	// the render-ignored `scale{x,y,z}`) is a transform field, so a write that sets it is a
	// transform-write and must carry provenance under the §2 rule, exactly like position/rotation.
	return !!(readVec3(row?.position) || readYaw(row?.rotation) !== null || Number.isFinite(row?.uniformScale));
}

/** §2 drift guard: a bare transform with NO provenance flag re-opens the class DECISION-050 closed
 * (stale free-placed instances). Callers warn + fall back to the anchor rather than honouring it. */
export function isUnflaggedTransform(row) {
	return hasTransform(row) && !isFreePlaced(row);
}

/**
 * Resolve one instance's effective transform: `row.<field> ?? shellAnchor.<field>`, PER FIELD.
 *
 * @param {object|null} row          instance row ({position?, rotation?{y}, knobs?{provenance}})
 * @param {object|null} shellAnchor  the GLB shell anchor ({position, rotationY})
 * @param {{honourUnflagged?: boolean}} [opts]  default false — an unflagged transform is ignored
 *        (anchor wins) and reported, per the §2 drift guard.
 * @returns {{position:{x,y,z}|null, rotationY:number|null, source:{position:string,rotationY:string},
 *            freePlaced:boolean, unflagged:boolean}}
 *        source values: "instance" | "anchor" | "none"
 */
export function resolveInstanceTransform(row, shellAnchor, opts = {}) {
	const unflagged = isUnflaggedTransform(row);
	const honour = unflagged ? opts.honourUnflagged === true : true;

	const rowPos = honour ? readVec3(row?.position) : null;
	const rowYaw = honour ? readYaw(row?.rotation) : null;
	const anchorPos = readVec3(shellAnchor?.position);
	const anchorYaw = Number.isFinite(shellAnchor?.rotationY) ? shellAnchor.rotationY : null;

	// `??` per field — a row that carries only a position still inherits the anchor's yaw.
	const position = rowPos ?? anchorPos ?? null;
	const rotationY = rowYaw ?? anchorYaw ?? null;

	return {
		position,
		rotationY,
		source: {
			position:  rowPos ? "instance" : (anchorPos ? "anchor" : "none"),
			rotationY: rowYaw !== null ? "instance" : (anchorYaw !== null ? "anchor" : "none"),
		},
		freePlaced: isFreePlaced(row),
		unflagged,
	};
}

/** 🔴 ASK-145 §13.4 — the pose a hydrated /instances row asks an ALREADY-COMPOSED fixture to stand at,
 * POSITION AND YAW. The hydrate applier used to resolve position only, so a fixture composed from a
 * boot whose mint lagged the merchant's rotate (the boot row still at the bonded yaw) was moved to her
 * position and never turned — measured on the Cloud Run sweep venue 4 of 5: stored 5π/4, drawn π.
 *
 * `entry` is the registered anchor entry: with a `_shellPose` the §2 precedence decides each field
 * (`row ?? shell`, unflagged transforms ignored); without one (a net-new placement, D-073) the row's
 * own fields ARE the pose. Null per field = "no opinion", never "zero". */
export function persistedPose(row, entry) {
	if (!row || !entry) return { position: null, rotationY: null };
	if (entry._shellPose) {
		const t = resolveInstanceTransform(row, entry._shellPose);
		return {
			position: t.source.position === "instance" && t.position ? t.position : (readVec3(entry._shellPose.position) || null),
			rotationY: Number.isFinite(t.rotationY) ? t.rotationY : null,
		};
	}
	return { position: readVec3(row.position), rotationY: readYaw(row.rotation) };
}

/** Smallest signed difference a − b on the circle, in (−π, π]. Yaws are compared mod 2π: π and −π are
 * one pose, and a turn applied for that difference would spin a fixture all the way round. */
export function yawDelta(a, b) {
	let d = (a - b) % (2 * Math.PI);
	if (d > Math.PI) d -= 2 * Math.PI;
	if (d <= -Math.PI) d += 2 * Math.PI;
	return d;
}

/** The `knobs` patch a MOVE must write so the persisted transform is a flagged, deliberate
 * free-place (§2) — merged over any existing knobs by the caller.
 *
 * ASK-106 §1: a committed placement also records WHAT STUCK — `{face, receiverKind, behaviour}`,
 * computed by the attachment grammar at the moment of the drop. It rides in `knobs` because that is
 * the ratified free-form channel of the instance write-shape (the same home provenance was re-aimed
 * at in 2026-07-24); the transform-write guard reads `knobs.provenance` and is unaffected. Omitted
 * entirely when the caller has no record, so a clear/legacy write is byte-identical to before. */
export function freePlaceKnobs(provenance = "merchant-move", attachment = null) {
	const knobs = { provenance: FREE_PLACE_PROVENANCE.includes(provenance) ? provenance : "merchant-move" };
	if (attachment && typeof attachment === "object" && attachment.face) {
		knobs.attachment = {
			face: attachment.face,
			receiverKind: attachment.receiverKind ?? null,
			behaviour: attachment.behaviour ?? null,
		};
	}
	return knobs;
}

/**
 * The FULL /instances write-shape a move must PUT.
 *
 * Two things this exists to get right, both learned the hard way:
 *
 * 1. FULL DOCUMENT, not a patch. GCG validates the whole record on PUT, so a bare
 *    `{position, knobs}` died on `400 — fixtureKind must match [a-z0-9-]{1,64}`: the field it never
 *    received. Sending the row means the write is also correct whether GCG merges or replaces.
 *
 * 2. THE REAL YAW, always. GCG normalises `rotation: {x:0, y:0, z:0}` onto any row it stores, and §2
 *    resolves `row.rotation ?? anchor.rotation` PER FIELD — so a stored y=0 is a real value that WINS
 *    over the shell anchor's authored yaw. On a shop where anchors carry rotation (the demo-store
 *    shell has seven: wall-shelves at 90°, stack-table at -90°, fan-table at 90°, signs), moving a
 *    fixture would silently straighten it on the next reload. That was unreachable while transforms
 *    weren't being applied at all; the moment moves started working, it became live data corruption.
 *    So a move carries the fixture's ACTUAL yaw, and refuses to write a transform without one rather
 *    than let the 0 default stand in for "unknown".
 *
 * @param {object} row        the existing instance row (identity + any prior fields)
 * @param {{position:{x,y,z}, rotationY:number, provenance?:string}} move
 * @returns {{body:object|null, reason:string|null}}  body is null when the move must NOT be written
 */
// NOTE the deliberate absence of a default on `host`: it must stay UNDEFINED when the caller does
// not pass one, because undefined (omit) and null (explicitly clear to shell root) are two
// different writes. A `host = null` default collapsed them and made every ordinary move send
// `host: null` — caught by the contract gate, not by review.
export function moveWriteShape(row, { position, rotationY, uniformScale, provenance = "merchant-move", attachment = null, host } = {}) {
	if (!row || typeof row !== "object") return { body: null, reason: "no instance row to write" };
	if (!readVec3(position)) return { body: null, reason: "move has no finite position" };
	if (!Number.isFinite(rotationY)) {
		// Deliberately a refusal, not a default. Writing 0 here is the corruption.
		return { body: null, reason: "move has no known yaw — refusing to write a transform that would reset rotation to 0" };
	}
	// D-061 §3 — `host` has THREE states on a write, and the difference between two of them is a
	// data-corruption bug the canonical fixture calls out by name:
	//
	//   undefined  → omit the field entirely. A row that never had a host stays byte-identical to
	//                every row written before D-061 existed — the zero-migration clause.
	//   "<key>"    → set it. `position` is HOST-LOCAL. A transform-write: needs knobs.provenance.
	//   null       → EXPLICITLY clear to shell root. This one cannot be an omission, because
	//                `...row` below spreads the row's existing host straight back into the body:
	//                omitting would keep the stale host while the position is now world-space, and
	//                the stored row would read a world position as module-local. Exempt from
	//                provenance, like every other clear (fixture: accept-clear-host-to-shell-root).
	//
	// The CALLER decides which, and must pass the matching coordinate space. See `hostWritePolicy`
	// for why sending host-LOCAL numbers is a measurement rather than a constant.
	const clearHost = host === null;
	const hostKey = isHostKey(host) ? host : null;
	return {
		body: {
			...row,
			// After the row spread so an explicit host wins over a stale one.
			...(hostKey ? { host: hostKey } : clearHost ? { host: null } : {}),
			position: { x: position.x, y: position.y, z: position.z },
			rotation: { x: 0, y: rotationY, z: 0 },
			// ASK-094: carry the uniform scale when the caller sets it (a scale-write, provenance
			// merchant-scale); else echo the row's existing value (a move preserves scale).
			...(Number.isFinite(uniformScale) ? { uniformScale } : {}),
			// Provenance rides in `knobs` — the DECISION-050 §2 CANONICAL home (GCG's transform-write
			// guard re-aimed here 2026-07-24, e6c6bdf; the 2026-07-23 top-level dual-stamp is retired).
			knobs: { ...(row.knobs || {}), ...freePlaceKnobs(provenance, attachment) },
		},
		reason: null,
	};
}

// ─── DECISION-061 — host-relative placements ─────────────────────────────────────────────────────
// "A placement is `{ host, local transform }` — never a bare world transform." Host is
// `MODULE_<entryId>` (a composed shell module) or a fixture `instanceId` (fixture-on-fixture);
// **absent host = shell root**, which is why every legacy row is already correct and there is no
// migration, ever. Moving the host moves its placements — the engine scene-graph rule.
//
// Kept PURE alongside the §2 rules for the same reason: the ratified contract is unit-tested exactly
// as written, and the THREE-side mount reads its answers from here.

/** Prefix that marks a host key as a composed-shell module rather than a fixture instance. */
export const MODULE_HOST_PREFIX = "MODULE_";

/** A host key is BOUNDED, per the canonical fixture's `reject-malformed-host` case: a non-empty
 *  string with no whitespace — *"never empty, never whitespace-bearing, never a blob that would read
 *  as a node name downstream and never be one"*. `MODULE_<entryId>` for a module (entryId opaque,
 *  persisted, never reused — ASK-109), any `instanceId` for fixture-on-fixture.
 *
 *  The pattern is GCG's, quoted from its own 400 (measured against gcg-dev 2026-08-25):
 *      host must be a `MODULE_<entryId>` module key or a fixture instanceId
 *      ([A-Za-z0-9][A-Za-z0-9_.:-]{0,191}), or null for a shell-root placement
 *  Matched here EXACTLY rather than approximated. A looser client predicate would let a key through
 *  that the wire refuses, which is a 400 in a merchant's console — the precise class this co-owned
 *  contract exists to end. Refused on both sides, deliberately. */
const HOST_KEY_RE = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,191}$/;
export function isHostKey(v) {
	return typeof v === "string" && HOST_KEY_RE.test(v);
}

export function isModuleHost(hostKey) {
	return isHostKey(hostKey) && hostKey.startsWith(MODULE_HOST_PREFIX);
}

/** The host a row is placed under, or null for shell-root (the legacy + default case). */
export function hostOf(row) {
	const h = row?.host;
	return isHostKey(h) ? h : null;
}

/**
 * Pick the host from a raycast hit's ancestor chain — the write-path rule in the D-061 wire entry:
 * *"Client computes host-local at place-time from the raycast hit's ancestor chain (`MODULE_*` group
 * or fixture root — the cook already guarantees these exist)."*
 *
 * PURE: takes the chain as plain `{name, userData}` descriptors, NEAREST FIRST, so the THREE side
 * passes `object.parent` walked upward and this module stays free of the engine.
 *
 * Nearest wins: dropping a lamp on a table inside a module hosts it on the TABLE, not the module —
 * that is what makes the table carry the lamp when the merchant slides the table.
 *
 * @param {Array<{name?:string, userData?:object}>} ancestors  nearest-first ancestor descriptors
 * @returns {string|null} the host key, or null for shell root
 */
export function hostFromAncestors(ancestors) {
	if (!Array.isArray(ancestors)) return null;
	for (const node of ancestors) {
		if (!node) continue;
		// A fixture instance root — the group the stage creates per placed/baked fixture. Its
		// instanceId is the host key (fixture-on-fixture), per D-061 §3.
		const instanceId = node.userData?.instanceId;
		if (isHostKey(instanceId)) return instanceId;
		const name = typeof node.name === "string" ? node.name : "";
		if (name.startsWith(MODULE_HOST_PREFIX) && name.length > MODULE_HOST_PREFIX.length) return name;
	}
	return null;
}

/**
 * Would placing `instanceId` under `hostKey` create a cycle? D-061 §3: *"Cycles refused at write."*
 *
 * Walks the host chain upward through the given rows. A row hosted on itself, or any loop reached
 * along the way, is refused — and a pre-existing loop in the DATA cannot hang this walk either
 * (`seen` guards it), because refusing a write is no use if the check never returns.
 *
 * @param {Array<object>} rows  all instance rows ({instanceId, host})
 * @returns {boolean} true when the placement must be REFUSED
 */
export function wouldCycle(rows, instanceId, hostKey) {
	if (!isHostKey(instanceId) || !isHostKey(hostKey)) return false;
	if (hostKey === instanceId) return true;
	if (isModuleHost(hostKey)) return false;             // a module is never itself hosted — no chain to walk
	const byId = new Map((Array.isArray(rows) ? rows : []).filter((r) => isHostKey(r?.instanceId)).map((r) => [r.instanceId, r]));
	const seen = new Set([instanceId]);
	let cur = hostKey;
	while (isHostKey(cur) && !isModuleHost(cur)) {
		if (cur === instanceId) return true;
		if (seen.has(cur)) return false;                   // a loop that does not pass through instanceId
		seen.add(cur);
		cur = hostOf(byId.get(cur)) ;
	}
	return false;
}

/**
 * Rows whose host no longer exists. D-061 §3 + D-045: *"Host removal → orphaned placements are
 * surfaced per refuse-with-hint, never silently dropped."*
 *
 * Returns the orphans with the host that went missing, so the surface can NAME it. Deliberately does
 * not mutate or drop anything — deciding is the merchant's, and silently dropping authored work is
 * refused outright (ASK-109).
 *
 * @param {Array<object>} rows            all instance rows
 * @param {Iterable<string>} liveHostKeys  hosts that exist in the current scene
 * @returns {Array<{instanceId:string, anchorName:string|null, host:string}>}
 */
export function orphanedPlacements(rows, liveHostKeys) {
	const live = new Set(liveHostKeys || []);
	return (Array.isArray(rows) ? rows : []).reduce((out, row) => {
		const host = hostOf(row);
		if (host && !live.has(host)) out.push({ instanceId: row.instanceId ?? null, anchorName: row.anchorName ?? null, host });
		return out;
	}, []);
}

/**
 * MAY this client write host-LOCAL coordinates yet? — feature detection, not a flag someone has to
 * remember to flip.
 *
 * Measured against gcg-dev 2026-08-23, before any of this was wired: a PUT carrying `host` returns
 * **200 and the stored row comes back without it** (`instanceId,fixtureKind,anchorName,knobs`).
 * Not a rejection — so writing `host` breaks nothing — but not persistence either.
 *
 * That combination is the dangerous one, and it is why this function exists rather than a constant.
 * If the client started writing host-LOCAL positions while the backend silently dropped the host,
 * every such row would be read back as a WORLD position: a fixture placed 0.4 m from its module's
 * origin would reappear 0.4 m from the shop origin. That is the same failure class as the yaw-0
 * corruption `moveWriteShape` already refuses — a value that is not wrong, in a space that is.
 *
 * So the rule is: **always send `host`; only send host-LOCAL coordinates once the backend has been
 * SEEN to echo one back.** Until then the write is world-space and byte-identical to today, and the
 * `host` field rides along inertly. The day GCG persists it, the first response that echoes `host`
 * flips this on by itself — no deploy, no config, no coordination window.
 *
 * @param {object|null} lastEchoedRow  the row GCG returned from the most recent write (or a hydrate)
 * @returns {boolean} true once host round-tripping is a fact rather than a hope
 */
export function hostRoundTripConfirmed(lastEchoedRow) {
	return isHostKey(lastEchoedRow?.host);
}

/**
 * What space should THIS write use, given what the backend has been observed to do?
 *
 * @param {{host:string|null, confirmed:boolean}} args
 * @returns {{host:string|null, space:"host-local"|"world", why:string}}
 */
export function hostWritePolicy({ host = null, confirmed = false, hadHost = false } = {}) {
	const hostKey = isHostKey(host) ? host : null;
	if (!hostKey) {
		// Moving OUT of a module back to shell root must send an explicit null — see moveWriteShape's
		// three-state note. A row that never had a host omits the field instead (undefined).
		return hadHost
			? { host: null, space: "world", why: "un-hosted to shell root — explicit null, because the row spread would carry the stale host" }
			: { host: undefined, space: "world", why: "shell-root placement, no host to clear — field omitted (D-061 §3 zero migration)" };
	}
	if (!confirmed) {
		return {
			host: hostKey,
			space: "world",
			why: "host sent, world coordinates kept: the backend has not yet been seen to persist `host`, " +
				"and host-local numbers stored as world numbers are silent corruption",
		};
	}
	return { host: hostKey, space: "host-local", why: "backend echoed `host` — placements are host-relative (D-061 §3)" };
}

function readVec3(v) {
	if (!v || typeof v !== "object") return null;
	const { x, y, z } = v;
	return (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) ? { x, y, z } : null;
}

function readYaw(rot) {
	if (rot == null) return null;
	if (Number.isFinite(rot)) return rot;                    // bare radians tolerated
	if (typeof rot === "object" && Number.isFinite(rot.y)) return rot.y;   // wire shape { y } (yaw-only, v1)
	return null;
}
